/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow

import type { Element } from 'react';

import React, { useState } from 'react';
import orderBy from 'lodash/fp/orderBy.js';
import pipe from 'lodash/fp/pipe.js';

import numeral from 'numeral';
import {
  HorizontalGridLines,
  VerticalGridLines,
  XAxis,
  XYPlot,
  YAxis,
  LineMarkSeries,
  DiscreteColorLegend,
  Hint,
} from 'react-vis';
import { formatLevelAsLocationToken } from '@hbrisk/sp3-risk-model-support/models/locationSpecifier/index.js';
import {
  mapFloorToPlotY,
  mapFloorsToPlotY,
  mapPlotYToFloor,
} from '#support/models/locationHelpers/floorPlotMapping.js';
import {
  colorLegend,
  hint,
} from '#components/pages/Models/Model/sections/support/plots/Plot.module.scss';
import { COLORS } from '#constants/plotColors.js';
import CustomAxisLabel, {
  defaultLeftOffset,
} from '#components/pages/Models/Model/sections/support/plots/CustomAxisLabel.jsx';
import IntensityName from '#components/support/forms/IntensityName/index.js';
import isFloor from '#support/models/isFloor.js';
import { edpsByKey } from '#constants/edps/index.js';

import type { EdpKey } from '#constants/edps/index.js';

type Response = {
  location: number,
  returnPeriod: number,
  median: number,
};

type Props = {|
  edpKey: EdpKey,
  responseDataByReturnPeriod: {
    [returnPeriod: number]: Array<Response>,
  },
  returnPeriods: Array<number>,
  maxMedian: number,
  maxLocation: number,
  locations: Array<number>,
|};

const Line = (points, color, returnPeriod, locationType, setTip) => (
  <LineMarkSeries
    key={returnPeriod}
    color={color}
    data={points}
    onValueMouseOver={value => setTip(value)}
    onValueMouseOut={() => setTip(false)}
    markStyle={{ opacity: 0.8 }}
    size={isFloor(locationType) ? 3 : 2}
  />
);

const getStoryUpperBoundaryLevel = level => (level === -1 ? 1 : level + 1);

// Convert response rows to plot-space coordinates.
// Floor EDPs plot one point per level; story EDPs duplicate each value across
// lower/upper floor boundaries so a story value spans its full vertical interval.
const mapDataToCoordinates = (response, locationType) =>
  pipe(
    data => orderBy('location', 'asc', data),
    orderedResponse =>
      orderedResponse.reduce(
        (acc, item) => [
          ...acc,
          {
            x: item.median,
            y: mapFloorToPlotY(item.location),
          },
          ...(isFloor(locationType)
            ? []
            : [
                {
                  x: item.median,
                  // Story levels skip 0, so the B1-to-G story spans -1 to 1.
                  y: mapFloorToPlotY(getStoryUpperBoundaryLevel(item.location)),
                },
              ]),
        ],
        []
      )
  )(response);

// Convert arbitrary coordinate sequences into orthogonal (horizontal+vertical)
// segments, used for non-floor response traces.
const mapCoordinatesToStepPath = (coordinates = []) => {
  if (coordinates.length < 2) {
    return coordinates;
  }

  return coordinates.slice(1).reduce(
    (acc, curr) => {
      const prev = acc[acc.length - 1];

      return [...acc, { x: curr.x, y: prev.y }, curr];
    },
    [coordinates[0]]
  );
};

const StructuralResponsePlot = (props: Props): Element<'div'> => {
  const { edpKey, returnPeriods, responseDataByReturnPeriod, maxMedian, maxLocation, locations } =
    props;

  const [tipId, setTip] = useState(false);

  const { locationType, isPercent, edpUnit, name } = edpsByKey[edpKey];

  const width = 600;
  const height = 400;

  const unit = isPercent ? '%' : edpUnit;
  const xAxisTitle = `${name} [${unit}]`;

  const numberOfStories = isFloor(locationType) ? maxLocation - 1 : maxLocation;

  const xAxisFormat = x => {
    const res = isPercent ? '0.00%' : '0.00';
    return numeral(x).format(res);
  };

  const formatFloorLabel = floor => formatLevelAsLocationToken(true, numberOfStories, floor);
  const yAxisFormat = plotY => formatFloorLabel(mapPlotYToFloor(plotY));

  // Non-floor traces use story intervals and therefore need one extra top tick
  // so the final interval lands on roof.
  const yTickValues = mapFloorsToPlotY([
    ...locations,
    ...(isFloor(locationType) ? [] : [maxLocation + 1]),
  ]);

  const legendItems = returnPeriods.map((returnPeriod, index) => ({
    title: (
      <span>
        <IntensityName includeUploads returnPeriod={returnPeriod} /> ({returnPeriod} yrs)
      </span>
    ),
    color: COLORS[index % 10],
    strokeWidth: 12,
  }));

  const leftMargin = 70;

  return (
    <div
      className="structural-response-plot"
      style={{ position: 'relative', width: width + 50, height: height + 50 }}
    >
      <XYPlot
        width={width}
        height={height}
        xDomain={[0, maxMedian]}
        onMouseLeave={() => setTip(false)}
        margin={{ left: leftMargin }}
      >
        <VerticalGridLines />
        <HorizontalGridLines />
        <XAxis tickFormat={xAxisFormat} />
        <YAxis tickValues={yTickValues} tickFormat={yAxisFormat} />

        {returnPeriods.map((returnPeriod, index) => {
          const data = responseDataByReturnPeriod[returnPeriod];
          const coordinates = mapDataToCoordinates(data, locationType);
          const points = isFloor(locationType)
            ? coordinates
            : mapCoordinatesToStepPath(coordinates);

          return Line(points, COLORS[index % 10], returnPeriod, locationType, setTip);
        })}

        {tipId ? (
          <Hint value={tipId} className={hint}>
            <div>
              <div>Floor: {yAxisFormat(tipId.y)}</div>
              <div>Median: {xAxisFormat(tipId.x)}</div>
            </div>
          </Hint>
        ) : null}

        <DiscreteColorLegend
          className={colorLegend}
          color="grey"
          width={200}
          items={legendItems}
          style={{
            position: 'absolute',
            top: '15px',
            left: `${width}px`,
          }}
        />
      </XYPlot>
      <CustomAxisLabel
        title="Floors"
        innerHeight={height}
        innerWidth={width}
        leftOffset={leftMargin + defaultLeftOffset + -15}
      />
      <CustomAxisLabel title={xAxisTitle} xAxis innerHeight={height} innerWidth={width} />
    </div>
  );
};

export default StructuralResponsePlot;
